// Pure manifest helpers — rune-free so node:test can load them without
// the Svelte compiler. The reactive store in catalogue-store.svelte.ts
// is the place for state; this module is the place for derivations that
// take a manifest and a coordinate and return data.

import type {
  Jurisdiction,
  ProductId,
  ManifestVersionEntry,
  VersionManifest
} from './types';

// Returns the manifest entry that was in effect (or would have been
// served) immediately before the given UTC instant for this product.
//
// Used by the compare-link affordance on Approved & Queued cards and
// Dashboard tiles: when a queued entry B sits between an earlier
// queued entry A and the current in-effect, the "before" snapshot the
// operator should diff against is A's snapshot — the one a customer
// would have received the moment before B's time gate fires — not the
// current in-effect, which carries A's edits B's snapshot never saw.
//
// Algorithm: among entries with status != dequeued and
// effective_from.utc strictly less than beforeUtc, pick the one with
// the latest effective_from.utc. Returns null when no such predecessor
// exists (rare; a bootstrap entry is always the floor unless the
// caller is asking about a moment before the product existed).
export function getPredecessorInEffectAt(
  manifest: VersionManifest | null,
  jurisdiction: Jurisdiction,
  product: ProductId,
  beforeUtc: string
): ManifestVersionEntry | null {
  if (!manifest) return null;
  const beforeMs = new Date(beforeUtc).getTime();
  if (!Number.isFinite(beforeMs)) return null;
  const versions = manifest.jurisdictions?.[jurisdiction]?.[product]?.versions || [];
  let best: ManifestVersionEntry | null = null;
  let bestMs = -Infinity;
  for (const v of versions) {
    if (v.status === 'dequeued') continue;
    const efMs = new Date(v.effective_from.utc).getTime();
    if (!Number.isFinite(efMs)) continue;
    if (efMs >= beforeMs) continue;
    if (efMs > bestMs) {
      best = v;
      bestMs = efMs;
    }
  }
  return best;
}
